import { isEqual, isPresent, logToExternalErrorHandlers, normalizePathname } from '@luminovo/commons';
import { useEffect, useRef, useState } from 'react';

const WINDOW_IN_MS = 1000;

/** Reports once to Sentry when a read-once prop (form defaults) deeply changes within a second of its first value. */
export function useAssertStableProp(prop: unknown, options?: { errorMessage?: string; enabled?: boolean }): void {
    const { errorMessage = 'Property changed within the first second', enabled = true } = options ?? {};

    const [baseline, setBaseline] = useState<{ value: unknown } | undefined>(() =>
        isPresent(prop) ? { value: prop } : undefined,
    );
    const [isWithinWindow, setIsWithinWindow] = useState(true);
    const hasReported = useRef(false);

    useEffect(() => {
        if (isPresent(baseline) || !isPresent(prop)) {
            return;
        }

        // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
        setBaseline({ value: prop });
    }, [prop, baseline]);

    // Keyed on the baseline: callers rebuild the prop each render, which would hold the window open.
    useEffect(() => {
        if (!isPresent(baseline)) {
            return;
        }

        const timer = setTimeout(() => {
            setIsWithinWindow(false);
        }, WINDOW_IN_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [baseline]);

    useEffect(() => {
        if (!enabled || hasReported.current || !isWithinWindow || !isPresent(baseline)) {
            return;
        }

        if (isEqual(baseline.value, prop)) {
            return;
        }

        hasReported.current = true;
        const diff = diffObject(baseline.value, prop);
        // oxlint-disable-next-line no-console
        console.error('useAssertStableProp (initial vs new):', diff);
        // Names only: the diff's values can hold customer data.
        const changedKeys = [...new Set(diff.map(({ path }) => path.split('.')[0]))];
        logToExternalErrorHandlers(new Error(errorMessage), {
            fingerprint: [normalizePathname()],
            extra: { changedKeys },
        });
    }, [prop, isWithinWindow, baseline, errorMessage, enabled]);
}

type DiffResult = {
    path: string;
    left: unknown;
    right: unknown;
};

function diffObject(left: unknown, right: unknown): Array<DiffResult> {
    const results: DiffResult[] = [];

    function recurse(currentLeft: unknown, currentRight: unknown, currentPath: string) {
        if (isEqual(currentLeft, currentRight)) {
            return; // No difference
        }

        // If both are objects and not null, we need to compare their keys and recurse
        if (isObject(currentLeft) && isObject(currentRight)) {
            const allKeys = new Set([...Object.keys(currentLeft), ...Object.keys(currentRight)]);
            for (let key of allKeys) {
                recurse(currentLeft[key], currentRight[key], currentPath ? `${currentPath}.${key}` : key);
            }
        } else {
            // Different or not both objects, record the difference
            results.push({
                path: currentPath,
                left: currentLeft,
                right: currentRight,
            });
        }
    }

    // A type predicate, so the recursive key access typechecks without widening to `any`.
    function isObject(value: unknown): value is Record<string, unknown> {
        return value !== null && typeof value === 'object';
    }

    recurse(left, right, ''); // Start the recursion with empty path
    return results;
}
